import DOMPurify from 'dompurify';
import { withAsciiDashes } from '../../utils/labelMatching';
import { MULTI_VALUE_SEPARATOR } from './columns';

/**
 * Words research output uses for "don't know" ("null" is how code and JSON write it). In any column
 * they mean the same as a blank cell, so they are cleared (with a warning) rather than failing as an
 * unknown option or being saved as text.
 */
export const PLACEHOLDER_WORDS = ['N/A', 'NA', 'Unknown', 'TBD', '-', 'None', 'null'] as const;
const PLACEHOLDER_VALUES = new Set(PLACEHOLDER_WORDS.map((word) => word.toLowerCase()));

/**
 * Comparison key for a choice value: typographic dashes folded to `-`, case, runs of whitespace and
 * spaces around a dash ignored. Both the uploaded value and every option go through it, so
 * `Lyophilization - Biologics` matches the stored `Lyophilization — Biologics`, `$50k–$150k` matches
 * `$50k - $150k` and `Fast (2 – 4 weeks)` matches `Fast (2-4 weeks)`. No two options of a list share a key.
 */
export function foldForMatching(value: string): string {
  return withAsciiDashes(value).trim().replace(/\s+/g, ' ').replace(/ ?- ?/g, '-').toLowerCase();
}

export function isPlaceholder(value: string): boolean {
  return PLACEHOLDER_VALUES.has(foldForMatching(value));
}

/**
 * The canonical option a value refers to, or null when it matches none. The returned string is the
 * option exactly as the form stores it — never the uploaded spelling.
 */
export function matchOption(value: string, options: readonly string[]): string | null {
  const key = foldForMatching(value);
  return options.find((option) => foldForMatching(option) === key) ?? null;
}

/**
 * `matchOption` that also ignores every space, for the budget and batch-size labels: their amounts
 * and units are written with or without one (`<$50k`, `Pilot Scale (1-10 kg)`). Still no two options
 * of those lists share a key, and a missing dash still does not match.
 */
export function matchOptionIgnoringSpaces(value: string, options: readonly string[]): string | null {
  const key = foldForMatching(value).replace(/ /g, '');
  return options.find((option) => foldForMatching(option).replace(/ /g, '') === key) ?? null;
}

/** Order-preserving de-duplication. */
export function dedupe<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}

/** Splits a multi-value cell on `|`, trimming each value and dropping empties and repeats. */
export function splitMultiValue(cell: string): string[] {
  return dedupe(
    cell
      .split(MULTI_VALUE_SEPARATOR)
      .map((value) => value.trim())
      .filter(Boolean)
  );
}

/**
 * Removes markup from a free-text value, with the same DOMPurify configuration the profile form's
 * `sanitizeInput` uses (no tags, no attributes).
 *
 * Returns the plain text rather than DOMPurify's serialised HTML: the serialised form re-encodes
 * `<`, `>` and `&` as entities whenever the input contained a tag, and these fields are rendered as
 * text, so "batches <1 kg" would otherwise display as "batches &lt;1 kg".
 */
export function sanitizeText(value: string): string {
  if (!value) return '';
  const fragment = DOMPurify.sanitize(value, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    RETURN_DOM_FRAGMENT: true,
  });
  return (fragment.textContent ?? '').trim();
}

/** Real calendar date written as YYYY-MM-DD. */
export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
